import React, { useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material'
import { useNodesOfTypeFromSource } from './hooks'
import { useParseNode } from './configBasedIngestion'
import { ISimplifiedNode } from '../../components/ingestion/IngestedTree'
import { PotentiallyHtml } from '../../components/PotentiallyHtml'
import { IndexControl } from './IndexControl'

export function IngestionPreview({ configKey }: { configKey: string }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const nodes = useNodesOfTypeFromSource(configKey)
  const { parseNode } = useParseNode({ skipChildren: true })

  const { ingested, title } = useMemo(() => {
    if (!nodes.length) return { ingested: null, title: null }
    const { data, title } = nodes[currentIndex]
    return {
      ingested: parseNode({ ...data }),
      title,
    }
  }, [parseNode, nodes, currentIndex])

  if (!ingested) return null

  return (
    <Accordion>
      <AccordionSummary>
        <Box>Ingestion Preview</Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {title}
          <IndexControl
            onIndexUpdate={setCurrentIndex}
            currentIndex={currentIndex}
            total={nodes.length}
          />
        </Box>
        <NodePreview node={ingested} />
      </AccordionDetails>
    </Accordion>
  )
}

function NodePreview({ node }: { node: ISimplifiedNode }) {
  return (
    <Box border={'solid 1px'}>
      <Box padding="10px">
        <Box>
          <strong>Type</strong>: {node.type}
        </Box>
      </Box>
      <NodeData node={node} />
      <NodeChildren node={node} />
    </Box>
  )
}

function NodeData({ node }: { node: ISimplifiedNode }) {
  const { value, description, ...rest } = node.data as Record<string, unknown>
  return (
    <Box padding="10px">
      <Box>
        <strong>Data</strong>
      </Box>
      {Object.entries(rest).map(([key, val]) => {
        return (
          <Box key={key}>
            <strong>{key}: </strong>
            {String(val)}
          </Box>
        )
      })}
      {!!value && <PotentiallyHtml s={String(value)} />}
      {!!description && <PotentiallyHtml s={String(description)} />}
    </Box>
  )
}

function NodeChildren({ node }: { node: ISimplifiedNode }) {
  if (!('nodes' in node) || !node.nodes?.length) return null
  return (
    <Box padding="10px">
      <Box>
        <strong>Children</strong>
      </Box>
      <Box padding="5px">
        {node.nodes?.map((n, i) => <NodePreview key={i} node={n} />)}
      </Box>
    </Box>
  )
}
